<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import moment from "moment";
import DateRangePicker from "daterangepicker";
import { ref, computed, onMounted } from "vue";

import { dailyReport as dailyReportConfig } from "./data";
import { useDailyReportStore } from "@/stores/dailyReportStore";
import { toast } from "@/utils/toast";
import DailyReportTable from "./daily-report-table.vue";

const title = "Daily Report";
const text = "HR";
const text1 = "Daily Report";

const store = useDailyReportStore();

const dateRangeInput = ref(null);
const sortLabel = ref("Last 7 Days");
const statusLabel = ref("Select Status");

const selectedYear = computed({
  get: () => store.year,
  set: (val) => (store.year = val),
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

// date range picker
function makeRangeLabel(start, end) {
  const s = start.format("DD/MM/YYYY");
  const e = end.format("DD/MM/YYYY");
  return `${s} - ${e}`;
}

function initDateRangePicker() {
  if (!dateRangeInput.value) return;

  const start = moment().subtract(6, "days");
  const end = moment();

  new DateRangePicker(
    dateRangeInput.value,
    {
      startDate: start,
      endDate: end,
      ranges: {
        Today: [moment(), moment()],
        Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [
          moment().subtract(1, "month").startOf("month"),
          moment().subtract(1, "month").endOf("month"),
        ],
      },
    },
    (start, end) => {
      dateRangeInput.value.value = makeRangeLabel(start, end);
      store.filters.date_from = start.format("YYYY-MM-DD");
      store.filters.date_to = end.format("YYYY-MM-DD");
      store.reload();
    }
  );

  // initial label + filters
  dateRangeInput.value.value = makeRangeLabel(start, end);
  store.filters.date_from = start.format("YYYY-MM-DD");
  store.filters.date_to = end.format("YYYY-MM-DD");
}

// chart (dynamic, with fallback config)
const dailyChart = computed(() => {
  const base = dailyReportConfig.sline || {};
  const categories =
    store.chartCategories.length > 0
      ? store.chartCategories
      : base.xaxis?.categories || [];
  const series =
    store.chartSeries.length > 0
      ? store.chartSeries
      : dailyReportConfig.series || [];

  return {
    options: {
      ...base,
      chart: {
        ...(base.chart || {}),
        type: "area",
        height: 190,
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      xaxis: {
        ...(base.xaxis || {}),
        categories,
      },
    },
    series,
  };
});

// years for "This Year" dropdown
const yearOptions = computed(() => {
  if (store.years && store.years.length) {
    return store.years;
  }
  const y = selectedYear.value;
  return [y, y - 1, y - 2];
});

function selectYear(y) {
  selectedYear.value = y;
  store.reload({ year: y });
}

// status filter
function setStatus(status, label) {
  store.filters.status = status;
  statusLabel.value = label;
  store.reload();
}

// sort dropdown
function setSort(key, label) {
  sortLabel.value = label;

  if (key === "recent") {
    store.filters.sort = "recent";
  } else if (key === "oldest") {
    store.filters.sort = "oldest";
  } else if (key === "last7") {
    const start = moment().subtract(6, "days");
    const end = moment();
    store.filters.date_from = start.format("YYYY-MM-DD");
    store.filters.date_to = end.format("YYYY-MM-DD");
  } else if (key === "lastMonth") {
    const start = moment().subtract(1, "month").startOf("month");
    const end = moment().subtract(1, "month").endOf("month");
    store.filters.date_from = start.format("YYYY-MM-DD");
    store.filters.date_to = end.format("YYYY-MM-DD");
  }

  store.reload();
}

// table pagination/sort change
function onTableChange(pagination) {
  store.filters.page = pagination.current;
  store.filters.per_page = pagination.pageSize;
  store.fetchList();
}

// export
async function downloadReport(format = "csv") {
  try {
    const res = await store.exportReport(format);

    const mime =
      format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const blob = new Blob([res.data], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const ext = format === "pdf" ? "pdf" : "csv";
    link.href = url;
    link.setAttribute(
      "download",
      `daily-report-${new Date().toISOString().slice(0, 10)}.${ext}`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      e.message ||
      "Failed to export daily report";
    toast(msg, "error");
  }
}

onMounted(async () => {
  initDateRangePicker();
  await store.reload();
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="downloadReport('pdf')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="downloadReport('csv')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row">
        <div class="col-xl-6 d-flex">
          <div class="row flex-fill dq-stat-grid">
            <!-- Total Present -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between"
                  >
                    <div>
                      <p class="fs-12 fw-normal mb-1 text-truncate">
                        Total Present
                      </p>
                      <h4>{{ store.summary.total_present }}</h4>
                    </div>
                    <div class="leave-report-icon">
                      <a href="javascript:void(0);">
                        <span
                          class="p-2 border border-primary bg-transparent-primary rounded-circle d-flex align-items-center justify-content-center"
                          ><i class="ti ti-user-check text-primary"></i
                        ></span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- /Total Present -->

            <!-- Completed Tasks -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between"
                  >
                    <div>
                      <p class="fs-12 fw-normal mb-1 text-truncate">
                        Completed Tasks
                      </p>
                      <h4>{{ store.summary.tasks_completed }}</h4>
                    </div>
                    <div class="leave-report-icon">
                      <a href="javascript:void(0);">
                        <span
                          class="p-2 border border-success bg-transparent-success rounded-circle d-flex align-items-center justify-content-center"
                          ><i class="ti ti-subtask text-success"></i
                        ></span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- /Completed Tasks -->

            <!-- Total Absent -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between"
                  >
                    <div>
                      <p class="fs-12 fw-normal mb-1 text-truncate">
                        Total Absent
                      </p>
                      <h4>{{ store.summary.total_absent }}</h4>
                    </div>
                    <div class="leave-report-icon">
                      <a href="javascript:void(0);">
                        <span
                          class="p-2 border border-danger bg-transparent-danger rounded-circle d-flex align-items-center justify-content-center"
                          ><i class="ti ti-user-x text-danger"></i
                        ></span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- /Total Absent -->

            <!-- Pending Tasks -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between"
                  >
                    <div>
                      <p class="fs-12 fw-normal mb-1 text-truncate">
                        Pending Tasks
                      </p>
                      <h4>{{ store.summary.tasks_pending }}</h4>
                    </div>
                    <div class="leave-report-icon">
                      <a href="javascript:void(0);">
                        <span
                          class="p-2 border border-skyblue bg-transparent-skyblue rounded-circle d-flex align-items-center justify-content-center"
                          ><i class="ti ti-user-x text-skyblue"></i
                        ></span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- /Pending Tasks -->
          </div>
        </div>

        <!-- Chart -->
        <div class="col-xl-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0 pb-0">
              <div
                class="d-flex flex-wrap justify-content-between align-items-center"
              >
                <div class="d-flex align-items-center">
                  <span class="me-2"
                    ><i class="ti ti-chart-bar text-danger"></i
                  ></span>
                  <h5>Daily Attendance</h5>
                </div>
                <div class="d-flex align-items-center">
                  <p class="d-inline-flex align-items-center me-2 mb-0">
                    <i class="ti ti-square-filled fs-12 text-success me-2"></i>
                    Present
                  </p>
                  <p class="d-inline-flex align-items-center mb-0 me-2">
                    <i class="ti ti-square-filled fs-12 text-danger me-2"></i>
                    Absent
                  </p>
                </div>
                <div class="dropdown">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ selectedYear }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li v-for="y in yearOptions" :key="y">
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="selectYear(y)"
                      >
                        {{ y }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="card-body py-0">
              <div id="daily-report">
                <apexchart
                  type="area"
                  height="190"
                  :options="dailyChart.options"
                  :series="dailyChart.series"
                ></apexchart>
              </div>
            </div>
          </div>
        </div>
        <!-- /Chart -->
      </div>

      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Daily Attendance List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3 dq-list-toolbar"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ statusLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('', 'All Status')"
                    >All Status</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Present', 'Present')"
                    >Present</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Absent', 'Absent')"
                    >Absent</a
                  >
                </li>
              </ul>
            </div>
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('recent', 'Recently Added')"
                    >Recently Added</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('oldest', 'Ascending')"
                    >Ascending</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('lastMonth', 'Last Month')"
                    >Last Month</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('last7', 'Last 7 Days')"
                    >Last 7 Days</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <daily-report-table
              :rows="store.items"
              :loading="store.loading"
              :total="store.pagination.total"
              :page-size="store.pagination.per_page"
              :current="store.pagination.current_page"
              @change="onTableChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
  <!-- /Page Wrapper -->
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control,
  .dq-list-toolbar .input-icon-end {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


